<template>
  <div class="page">
    <section class="layout">
      <!-- 左侧查询 -->
      <div class="search-panel">
        <div class="input-wrap">
          <textarea
            v-model.trim="keyword"
            placeholder="输入参考号/运单号/转单号，每行一个或单条"
            rows="10"
          ></textarea>
          <span class="required">*</span>
        </div>
        <div class="search-actions">
          <label class="toggle-switch">
            <input type="checkbox" v-model="fuzzy" />
            <span class="slider"></span>
          </label>
          <span class="toggle-label">模糊查询</span>
          <button class="primary" :disabled="loading" @click="handleSearch">{{ loading ? '查询中...' : '查询' }}</button>
          <button class="arrow-btn" @click="handleSearch">›</button>
        </div>
        <p v-if="error" class="error">{{ error }}</p>
      </div>

      <!-- 右侧结果 -->
      <div class="result-panel" v-if="orders.length > 0">
        <!-- 订单列表 -->
        <div v-for="(order, index) in orders" :key="order.id" class="order-item">
          <!-- 顶部表格 -->
          <div class="info-table">
            <div class="table-header" v-if="index === 0">
              <span class="col col-expand"></span>
              <span class="col">客户参考号</span>
              <span class="col">运单号</span>
              <span class="col">转单号</span>
              <span class="col">总件数</span>
              <span class="col">目的地</span>
              <span class="col">收件人邮编</span>
            </div>
            <div class="table-row" :class="{ expanded: expandedOrders[order.id] }" @click="toggleOrder(order.id)">
              <span class="col col-expand">
                <span class="expand-icon">{{ expandedOrders[order.id] ? '∨' : '›' }}</span>
              </span>
              <span class="col">{{ order.customer_ref }}</span>
              <span class="col link">{{ order.waybill_no }}</span>
              <span class="col">{{ order.transfer_no || '-' }}</span>
              <span class="col">{{ order.total_pieces }}</span>
              <span class="col">{{ order.destination }}</span>
              <span class="col">{{ order.recipient_zip || '' }}</span>
            </div>
          </div>

          <!-- 展开详情 -->
          <div class="detail-section" v-if="expandedOrders[order.id]">
            <!-- 流程进度条 -->
            <div class="progress-bar">
              <div class="progress-line"></div>
              <div class="nodes">
                <div
                  v-for="node in getTimelineNodes(order)"
                  :key="node.key"
                  class="node"
                  :class="{ done: node.done }"
                >
                  <div class="node-icon">
                    <img :src="node.iconSrc" :alt="node.label" />
                  </div>
                  <div class="node-info">
                    <div class="node-label">{{ node.label }}</div>
                    <div class="node-sublabel">{{ node.sublabel }}</div>
                  </div>
                  <div class="node-time">{{ node.timeLabel }}</div>
                </div>
              </div>
            </div>

            <!-- 状态时间线 -->
            <div class="record-list">
              <div
                v-for="(rec, idx) in getSortedRecords(order)"
                :key="rec.id || idx"
                class="record-row"
              >
                <span class="dot"></span>
                <span class="rec-time"><strong>{{ formatDateTime(rec.event_time) }}</strong></span>
                <span class="rec-desc">{{ rec.description || rec.status }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="empty" v-else>请输入单号查询</div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

// 状态配置：标签、子标签、图标
const STATUS_CONFIG = [
  { key: '入库', label: '入库', sublabel: '收货时间', icon: 'inbound' },
  { key: '出库', label: '出库', sublabel: '发货时间', icon: 'outbound' },
  { key: '起运地', label: '起运地', sublabel: '起飞时间', icon: 'origin' },
  { key: '目的地', label: '目的地', sublabel: '飞机落地时间', icon: 'destination' },
  { key: '派送', label: '派送', sublabel: '实际提取时间', icon: 'delivery' },
  { key: '签收', label: '签收', sublabel: '签收时间', icon: 'signed' },
]

// SVG图标（内联base64，避免外部依赖）
const ICON_SVGS = {
  inbound: `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIj48cGF0aCBkPSJNMyA5bDktNyA5IDd2MTFhMiAyIDAgMCAxLTIgMkg1YTIgMiAwIDAgMS0yLTJ6Ii8+PHBvbHlsaW5lIHBvaW50cz0iOSAyMiA5IDEyIDE1IDEyIDE1IDIyIi8+PC9zdmc+`,
  outbound: `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIj48cGF0aCBkPSJNMyA5bDktNyA5IDd2MTFhMiAyIDAgMCAxLTIgMkg1YTIgMiAwIDAgMS0yLTJ6Ii8+PHBhdGggZD0iTTkgMjJWMTJoNnYxMCIvPjxwYXRoIGQ9Ik0xNiA2bDQgNCIvPjxwYXRoIGQ9Ik0yMCA2bC00IDQiLz48L3N2Zz4=`,
  origin: `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIj48cmVjdCB4PSIxIiB5PSIzIiB3aWR0aD0iMTUiIGhlaWdodD0iMTMiIHJ4PSIyIi8+PHBhdGggZD0iTTE2IDhoNWwyIDN2NWgtN1Y4eiIvPjxjaXJjbGUgY3g9IjUuNSIgY3k9IjE4LjUiIHI9IjIuNSIvPjxjaXJjbGUgY3g9IjE4LjUiIGN5PSIxOC41IiByPSIyLjUiLz48L3N2Zz4=`,
  destination: `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIj48cmVjdCB4PSIxIiB5PSIzIiB3aWR0aD0iMTUiIGhlaWdodD0iMTMiIHJ4PSIyIi8+PHBhdGggZD0iTTE2IDhoNWwyIDN2NWgtN1Y4eiIvPjxjaXJjbGUgY3g9IjUuNSIgY3k9IjE4LjUiIHI9IjIuNSIvPjxjaXJjbGUgY3g9IjE4LjUiIGN5PSIxOC41IiByPSIyLjUiLz48L3N2Zz4=`,
  delivery: `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIj48cmVjdCB4PSIxIiB5PSIzIiB3aWR0aD0iMTUiIGhlaWdodD0iMTMiIHJ4PSIyIi8+PHBhdGggZD0iTTE2IDhoNWwyIDN2NWgtN1Y4eiIvPjxjaXJjbGUgY3g9IjUuNSIgY3k9IjE4LjUiIHI9IjIuNSIvPjxjaXJjbGUgY3g9IjE4LjUiIGN5PSIxOC41IiByPSIyLjUiLz48L3N2Zz4=`,
  signed: `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIj48cGF0aCBkPSJNOSAxMWwzIDNMIDIyIDQiLz48cGF0aCBkPSJNMjEgMTJ2N2EyIDIgMCAwIDEtMiAySDVhMiAyIDAgMCAxLTItMlY1YTIgMiAwIDAgMSAyLTJoMTEiLz48L3N2Zz4=`,
}

export default {
  name: 'HomePage',
  data() {
    return {
      keyword: '',
      fuzzy: false,
      loading: false,
      error: '',
      orders: [],
      expandedOrders: {},
    }
  },
  methods: {
    logout() {
      localStorage.removeItem('authUser')
      this.$router.push('/login')
    },
    formatDateTime(val) {
      if (!val) return ''
      const d = new Date(val)
      const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
      return `${date} ${time}`
    },
    toggleOrder(orderId) {
      this.$set(this.expandedOrders, orderId, !this.expandedOrders[orderId])
    },
    getSortedRecords(order) {
      if (!order || !Array.isArray(order.records)) return []
      return [...order.records].sort((a, b) => new Date(b.event_time) - new Date(a.event_time))
    },
    getTimelineNodes(order) {
      const timeFields = {
        '入库': 'inbound_time',
        '出库': 'outbound_time',
        '起运地': 'origin_time',
        '目的地': 'destination_time',
        '派送': 'delivery_time',
        '签收': 'signed_time',
      }
      return STATUS_CONFIG.map(cfg => {
        const timeField = timeFields[cfg.key]
        const timeValue = order ? order[timeField] : null
        return {
          key: cfg.key,
          label: cfg.label,
          sublabel: cfg.sublabel,
          iconSrc: ICON_SVGS[cfg.icon],
          done: !!timeValue,
          timeLabel: timeValue ? this.formatDateTime(timeValue) : '',
        }
      })
    },
    async handleSearch() {
      this.error = ''
      this.orders = []
      this.expandedOrders = {}
      if (!this.keyword) {
        this.error = '请输入查询关键字'
        return
      }
      this.loading = true
      try {
        // 按行分割关键字
        const keywords = this.keyword.split('\n').map(k => k.trim()).filter(k => k)
        const allOrders = []
        
        // 逐个查询
        for (const kw of keywords) {
          const resp = await axios.get('/api/orders/search', {
            params: { keyword: kw, fuzzy: this.fuzzy ? 1 : 0 },
          })
          const list = resp.data.data || []
          allOrders.push(...list)
        }
        
        if (!allOrders.length) {
          this.error = '未找到相关订单'
          return
        }
        
        // 去重
        const uniqueOrders = []
        const seenIds = new Set()
        for (const order of allOrders) {
          if (!seenIds.has(order.id)) {
            seenIds.add(order.id)
            uniqueOrders.push(order)
            this.$set(this.expandedOrders, order.id, false)
          }
        }
        
        this.orders = uniqueOrders
      } catch (err) {
        this.error = '查询失败，请稍后再试'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* 主色调：绿色 */
:root {
  --primary: #00a854;
  --primary-light: #e6f7ed;
  --border: #e0e0e0;
  --text: #333;
  --text-light: #666;
  --text-muted: #999;
}

.page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Microsoft YaHei', sans-serif;
  font-size: 13px;
  color: #333;
}

.layout {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 0;
  border: 1px solid #e0e0e0;
  background: #fff;
}

/* 左侧查询面板 */
.search-panel {
  padding: 16px;
  border-right: 1px solid #e0e0e0;
  background: #fafafa;
}

.input-wrap {
  position: relative;
}

.input-wrap .required {
  position: absolute;
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  color: #f5222d;
  font-size: 14px;
}

textarea {
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 8px;
  resize: vertical;
  font-size: 13px;
  box-sizing: border-box;
}

textarea:focus {
  border-color: #00a854;
  outline: none;
}

.search-actions {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 开关样式 */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 18px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 18px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.toggle-switch input:checked + .slider {
  background-color: #00a854;
}

.toggle-switch input:checked + .slider:before {
  transform: translateX(18px);
}

.toggle-label {
  font-size: 13px;
  color: #666;
  margin-right: auto;
}

button.primary {
  height: 32px;
  background: #00a854;
  border: none;
  color: #fff;
  padding: 0 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}

button.primary:hover {
  background: #00994d;
}

button.primary[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

button.arrow-btn {
  width: 32px;
  height: 32px;
  background: #00a854;
  border: none;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

button.arrow-btn:hover {
  background: #00994d;
}

.error {
  color: #f5222d;
  margin-top: 8px;
  font-size: 12px;
}

/* 右侧结果面板 */
.result-panel {
  background: #fff;
  min-height: 400px;
}

/* 订单项 */
.order-item {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.order-item:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

/* 表格样式 */
.table-header {
  display: grid;
  grid-template-columns: 40px 1fr 1fr 1fr 80px 80px 100px;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 500;
  color: #333;
}

.table-header .col {
  padding: 10px 8px;
  border-right: 1px solid #e0e0e0;
  font-size: 13px;
}

.table-header .col:last-child {
  border-right: none;
}

.table-row {
  display: grid;
  grid-template-columns: 40px 1fr 1fr 1fr 80px 80px 100px;
  cursor: pointer;
  transition: background 0.2s;
}

.table-row:hover {
  background: #f5f5f5;
}

.table-row .col {
  padding: 10px 8px;
  border-right: 1px solid #e0e0e0;
  font-size: 13px;
  display: flex;
  align-items: center;
}

.table-row .col:last-child {
  border-right: none;
}

.col-expand {
  justify-content: center;
}

.expand-icon {
  color: #00a854;
  font-weight: bold;
  font-size: 16px;
}

.col.link {
  color: #00a854;
  text-decoration: underline;
}

/* 展开详情区域 */
.detail-section {
  padding: 16px;
  border: 2px dashed #008f80;
  border-radius: 4px;
  margin-top: 8px;
}

/* 进度条 */
.progress-bar {
  position: relative;
  padding: 20px 0;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
}

.progress-line {
  position: absolute;
  top: 40px;
  left: 60px;
  right: 60px;
  height: 2px;
  background: #00a854;
}

.nodes {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  position: relative;
  z-index: 1;
}

.node {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.node-icon {
  width: 48px;
  height: 48px;
  background: #fff;
  border: 2px solid #d9d9d9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.node-icon img {
  width: 28px;
  height: 28px;
  opacity: 0.4;
}

.node.done .node-icon {
  border-color: #00a854;
  background: #e6f7ed;
}

.node.done .node-icon img {
  opacity: 1;
  filter: invert(42%) sepia(93%) saturate(1352%) hue-rotate(120deg) brightness(95%) contrast(101%);
}

.node-info {
  margin-bottom: 4px;
}

.node-label {
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.node-sublabel {
  font-size: 11px;
  color: #999;
}

.node-time {
  font-size: 11px;
  color: #00a854;
  min-height: 28px;
}

/* 记录列表 */
.record-list {
  padding: 0;
  max-height: 400px;
  overflow-y: auto;
  position: relative;
}

.record-list::before {
  content: '';
  position: absolute;
  left: 6px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e0e0e0;
}

.record-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0 8px 20px;
  border-bottom: none;
  position: relative;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #00a854;
  background: #fff;
  margin-top: 4px;
  margin-left: -20px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.rec-time {
  color: #333;
  font-size: 13px;
  white-space: nowrap;
}

.rec-time strong {
  font-weight: 600;
}

.rec-desc {
  color: #666;
  font-size: 13px;
}

.empty {
  color: #999;
  padding: 40px;
  text-align: center;
}
</style>

